import api from '../utils/ky';
import {
  MeetingParticipant,
  MeetingAnalysisResult,
  SkillAssessment,
  TranscriptMessage,
} from '../types/meetinganalysis';

/**
 * Extract participants from a meeting transcript
 * @param transcript The meeting transcript text
 * @returns Promise resolving to an array of participant objects
 */
export const extractParticipants = async (
  transcript: string,
): Promise<MeetingParticipant[]> => {
  try {
    const response = await api
      .post('api/meetingcall/extract-participants', {
        json: { transcript },
      })
      .json<{ participants: MeetingParticipant[] }>();

    return response.participants;
  } catch (error) {
    console.error('Error extracting participants:', error);
    throw new Error('Failed to extract participants from transcript');
  }
};

/**
 * Analyze a meeting transcript for insights about a specific participant
 * @param transcript The meeting transcript
 * @param participantName The name of the participant to analyze
 * @returns Promise resolving to the analysis result and ID
 */
export const analyzeMeeting = async (
  transcript: string,
  selectedParticipant: string,
  participants: string[],
): Promise<{ analysisId: string; result: MeetingAnalysisResult }> => {
  try {
    const response = await api
      .post('api/meetingcall/analyze', {
        json: { transcript, selectedParticipant, participants },
        timeout: 60000,
      })
      .json<{ analysisId: string; result: MeetingAnalysisResult }>();

    return response;
  } catch (error) {
    console.error('Error analyzing meeting:', error);
    throw new Error('Failed to analyze meeting transcript');
  }
};

/**
 * Fetch analysis by ID from the database
 * @param analysisId The ID of the analysis to fetch
 * @returns Promise resolving to the analysis data
 */
export const fetchAnalysisById = async (
  analysisId: string,
): Promise<MeetingAnalysisResult> => {
  try {
    const response = await api
      .get(`api/meetingcall/analysis/${analysisId}`)
      .json<MeetingAnalysisResult>();

    return response;
  } catch (error) {
    console.error('Error fetching analysis:', error);
    throw new Error('Failed to fetch analysis data');
  }
};

/**
 * Fetch skills assessment for a meeting analysis
 * @param analysisId The ID of the analysis to fetch skills for
 * @returns Promise resolving to the skills assessment data
 */
export const fetchSkillsAssessment = async (
  analysisId: string,
): Promise<SkillAssessment[]> => {
  try {
    const response = await api
      .get(`api/meetingcall/analysis/${analysisId}/skills`, { timeout: 30000 })
      .json<{ skillsAssessment: SkillAssessment[] }>();

    return response.skillsAssessment;
  } catch (error) {
    console.error('Error fetching skills assessment:', error);
    throw new Error('Failed to fetch skills assessment data');
  }
};

/**
 * Fetch transcript analysis for a meeting
 * @param analysisId The ID of the analysis to fetch transcript for
 * @returns Promise resolving to the transcript analysis data
 */
export const fetchTranscriptAnalysis = async (
  analysisId: string,
): Promise<TranscriptMessage[]> => {
  try {
    const response = await api
      .get(`api/meetingcall/analysis/${analysisId}/transcript`, {
        timeout: 60000,
      })
      .json<{ transcriptAnalysis: TranscriptMessage[] }>();

    return response.transcriptAnalysis;
  } catch (error) {
    console.error('Error fetching transcript analysis:', error);
    throw new Error('Failed to fetch transcript analysis');
  }
};

/**
 * Fetch conversation dynamics analysis for a meeting
 * @param analysisId The ID of the analysis to fetch dynamics for
 * @returns Promise resolving to the conversation dynamics analysis data
 */
export const fetchConversationDynamics = async (
  analysisId: string,
): Promise<any> => {
  try {
    const response = await api
      .get(`api/meetingcall/analysis/${analysisId}/dynamics`, {
        timeout: 60000,
      })
      .json<{ conversationDynamics: any }>();

    return response.conversationDynamics;
  } catch (error) {
    console.error('Error fetching conversation dynamics analysis:', error);
    throw new Error('Failed to fetch conversation dynamics analysis');
  }
};
